import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { usePageStore } from '@/store/pageStore'
import { usePageTitle } from '@/hooks/usePageTitle'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'
import { useTranslation } from 'react-i18next'
import { ModeSwitcher } from './ModeSwitcher'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import {
  LayoutDashboard,
  Users,
  Building2,
  BarChart3,
  Brain,
  LogOut,
  ChevronRight,
  Shield,
  Package,
  PanelLeftClose,
  PanelLeft,
  UsersRound,
  GraduationCap,
  StickyNote,
  ExternalLink,
  Mail,
  UserCircle,
  Headphones,
  FileText,
  ClipboardCheck
} from 'lucide-react'
import { clsx } from 'clsx'

// Company 전용 사이드바 메뉴 정의
interface SidebarItem {
  path: string
  labelKey: string
  icon: React.ComponentType<{ className?: string }>
  exact?: boolean
  devOnly?: boolean   // 개발 환경(localhost)에서만 노출 — Super Admin 링크와 동일 조건
}

// Super Admin 링크와 동일한 조건: Vite dev 또는 localhost 호스트
const isDevEnv = import.meta.env.DEV || window.location.hostname === 'localhost'

// Company 전용 고정 메뉴 (labelKey = nav:sidebar.* 키)
const sidebarItemsAll: SidebarItem[] = [
  { path: '/admin', labelKey: 'sidebar.dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/departments', labelKey: 'sidebar.departments', icon: Building2 },
  { path: '/admin/members', labelKey: 'sidebar.members', icon: Users },
  { path: '/admin/special-groups', labelKey: 'sidebar.specialGroups', icon: UsersRound },
  { path: '/admin/classes', labelKey: 'sidebar.classes', icon: GraduationCap },
  { path: '/admin/products', labelKey: 'sidebar.products', icon: Package },
  { path: '/admin/reports', labelKey: 'sidebar.reports', icon: BarChart3 },
  // { path: '/admin/aiq-report', labelKey: 'sidebar.aiqReport', icon: Brain },
  { path: '/admin/messages', labelKey: 'sidebar.messages', icon: Mail },
  { path: '/admin/contract', labelKey: 'sidebar.contract', icon: FileText },
  { path: '/admin/support', labelKey: 'sidebar.support', icon: Headphones },
  { path: '/admin/notes', labelKey: 'sidebar.notes', icon: StickyNote, devOnly: true },
]

const sidebarItems: SidebarItem[] = sidebarItemsAll.filter((item) => !item.devOnly || isDevEnv)

export default function AdminLayout() {
  const { user, logout } = useAuthStore()
  const { tenantInfo } = useTenantStore()
  const { pageSubtitle } = usePageStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const { t } = useTranslation('nav')

  // 테넌트 로고 URL (API 값 또는 기본 로고)
  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  // 페이지 타이틀 자동 업데이트
  usePageTitle()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const companyName = tenantInfo?.displayName || tenantInfo?.name || '회사'

  // 현재 경로에 해당하는 사이드바 메뉴 라벨 찾기
  const getCurrentMenuLabel = () => {
    // 정확히 일치하는 항목 먼저 찾기
    const exactMatch = sidebarItems.find(item => item.exact && location.pathname === item.path)
    if (exactMatch) return t(exactMatch.labelKey)

    // prefix 매칭 (가장 긴 경로 우선)
    const prefixMatches = sidebarItems
      .filter(item => !item.exact && location.pathname.startsWith(item.path))
      .sort((a, b) => b.path.length - a.path.length)

    return prefixMatches[0] ? t(prefixMatches[0].labelKey) : null
  }

  // 헤더에 표시할 제목: pageSubtitle > 현재 메뉴 라벨
  const headerTitle = pageSubtitle || getCurrentMenuLabel()

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Header Bar - 회사 이름 표시 */}
      <header
        className="fixed top-0 left-0 right-0 h-12 z-50 flex items-center px-6"
        style={{ backgroundColor: tenantInfo?.primaryColor || '#6366f1' }}
      >
        <div className="flex items-center space-x-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantInfo?.name || 'Quebon'}
              className="h-7 w-auto"
              onError={(e) => {
                // 로고 로딩 실패 시 기본 로고로 대체
                (e.target as HTMLImageElement).src = '/quebon-logo.svg'
              }}
            />
          ) : (
            <div className="h-7 w-7 rounded bg-white/20 flex items-center justify-center text-white font-bold text-sm">
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <span className="font-bold text-white text-lg">
            {companyName}
          </span>
          <span className="text-white/70 text-sm">{t('header.educationSystem')}</span>
          {headerTitle && (
            <>
              <span className="text-white/50">|</span>
              <span className="text-white font-medium">{headerTitle}</span>
            </>
          )}
        </div>
        <div className="ml-auto flex items-center space-x-4">
          {user?.email?.endsWith('@quebon.com') && (
            <Link
              to="/review-contents"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors text-sm font-medium"
            >
              <ClipboardCheck className="w-4 h-4" />
              <span>{t('header.contentReview')}</span>
            </Link>
          )}
          <ModeSwitcher />
          <LanguageSwitcher />
          <div className="flex items-center space-x-2 text-white/90">
            <Shield className="w-4 h-4" />
            <span className="text-sm">{t('header.admin')}</span>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={clsx(
        "fixed inset-y-0 left-0 bg-white shadow-lg pt-12 transition-all duration-300 flex flex-col",
        sidebarCollapsed ? "w-16" : "w-64"
      )}>
        {/* Logo Section */}
        <div className="h-16 flex items-center px-3 border-b bg-gray-50 flex-shrink-0">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
            title={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
          >
            {sidebarCollapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
          {!sidebarCollapsed && (
            <p className="font-semibold text-gray-900 ml-2">{t('header.adminConsole')}</p>
          )}
        </div>

        {/* 외부 링크 (개발 환경에서만) */}
        {isDevEnv && (
          <div className="px-2 py-3 border-b border-gray-200 flex-shrink-0">
            <a
              href="http://localhost:3001"
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(
                'flex items-center rounded-lg text-sm font-medium transition-colors',
                sidebarCollapsed ? 'px-3 py-2 justify-center' : 'px-3 py-2',
                'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              )}
              title="Super Admin"
            >
              <ExternalLink className={clsx('w-5 h-5', !sidebarCollapsed && 'mr-3')} />
              {!sidebarCollapsed && <span>Super Admin</span>}
            </a>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto mt-6 px-2">
          <div className="space-y-1">
            {sidebarItems.map((item) => {
                const Icon = item.icon
                const active = isActive(item.path, item.exact)

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={sidebarCollapsed ? t(item.labelKey) : undefined}
                    className={clsx(
                      'flex items-center rounded-lg text-sm font-medium transition-colors',
                      sidebarCollapsed ? 'px-3 py-2.5 justify-center' : 'px-3 py-2.5',
                      active
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )}
                  >
                    <Icon className={clsx(
                      'w-5 h-5',
                      !sidebarCollapsed && 'mr-3',
                      active ? 'text-primary-600' : 'text-gray-400'
                    )} />
                    {!sidebarCollapsed && (
                      <>
                        {t(item.labelKey)}
                        {active && (
                          <ChevronRight className="w-4 h-4 ml-auto text-primary-600" />
                        )}
                      </>
                    )}
                  </Link>
                )
              })}
          </div>
        </nav>

        {/* User Info */}
        <div className="p-3 border-t bg-gray-50 flex-shrink-0">
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Link
                to="/admin/profile"
                className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center hover:bg-primary-200 transition-colors"
                title={t('header.myProfile')}
              >
                <span className="text-sm font-medium text-primary-600">
                  {user?.fullName?.charAt(0) || 'U'}
                </span>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title={t('header.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Link to="/admin/profile" className="flex items-center min-w-0 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-600">
                    {user?.fullName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title={t('header.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={clsx(
        "pt-12 transition-all duration-300",
        sidebarCollapsed ? "pl-16" : "pl-64"
      )}>
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
